import { useContext, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Step1PersonalInfo, StepIndicator, Step2HowFoundUs, Step3AccountType } from './form-steps';
import { Step4Role } from './form-steps/Step4Role';
import { useUserInfoForm, InitialUserData } from './hooks/useUserInfoForm';
import { AuthContext } from './AuthProvider';

interface UserInfoFormProps {
  initialDisplayName?: string;
  onComplete: () => void;
  onError: (error: string) => void;
}

export default function UserInfoForm({
  initialDisplayName = '',
  onComplete,
  onError,
}: UserInfoFormProps) {
  const { user: supabaseUser } = useContext(AuthContext);

  // State for wiggle animation
  const [wiggleFields, setWiggleFields] = useState<Set<string>>(new Set());

  // Extract all existing user data for pre-filling
  const getInitialUserData = (): InitialUserData => {
    if (!supabaseUser?.user_metadata) {
      return { displayName: initialDisplayName };
    }

    const metadata = supabaseUser.user_metadata;
    return {
      displayName:
        metadata.full_name || metadata.name || metadata.display_name || initialDisplayName,
      country: metadata.country || '',
      howFoundUs: metadata.how_found_us || '',
      userType: metadata.user_type || '',
      companyName: metadata.company_name || '',
      roleDescription: metadata.role_description || '',
      companySegment: metadata.company_segment || '',
      howFoundUsOther: metadata.how_found_us_other || '',
      companySegmentOther: metadata.company_segment_other || '',
      useCase: metadata.use_case || [],
      useCaseOther: metadata.use_case_other || '',
      goals: metadata.goals || [],
      goalsOther: metadata.goals_other || '',
    };
  };

  const {
    currentStep,
    formData,
    loading,
    isCurrentStepValid,
    isFormValid,
    updateField,
    nextStep,
    prevStep,
    submitForm,
  } = useUserInfoForm(getInitialUserData());

  // Function to identify missing fields for current step
  const getMissingFields = (): string[] => {
    const missing: string[] = [];

    if (currentStep === 1) {
      if (!formData.displayName.trim()) missing.push('displayName');
      if (!formData.country) missing.push('country');
    } else if (currentStep === 2) {
      if (!formData.howFoundUs) missing.push('howFoundUs');
    } else if (currentStep === 3) {
      if (!formData.userType) missing.push('userType');
      if (formData.userType === 'business' && !formData.companyName.trim())
        missing.push('companyName');
      if (formData.userType === 'business' && !formData.companySegment)
        missing.push('companySegment');
    } else if (currentStep === 4) {
      if (!formData.roleDescription) missing.push('roleDescription');
      if (formData.useCase.length === 0) missing.push('useCase');
      if (formData.goals.length === 0) missing.push('goals');
    }

    return missing;
  };

  // Function to trigger wiggle animation
  const triggerWiggle = (fieldNames: string[]) => {
    const newWiggleFields = new Set(fieldNames);
    setWiggleFields(newWiggleFields);

    // Clear wiggle after animation duration
    setTimeout(() => {
      setWiggleFields(new Set());
    }, 600); // 600ms matches our CSS animation duration
  };

  // Handle click on disabled Next button
  const handleDisabledNextClick = () => {
    if (!isCurrentStepValid) {
      const missingFields = getMissingFields();
      triggerWiggle(missingFields);
    }
  };

  // Handle click on disabled Complete button
  const handleDisabledCompleteClick = () => {
    if (!isCurrentStepValid || !isFormValid) {
      const missingFields = getMissingFields();
      triggerWiggle(missingFields);
    }
  };

  const handleNext = () => {
    nextStep();
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const native = e.nativeEvent as SubmitEvent;
    const submitter = native?.submitter as HTMLButtonElement | null;
    // Only allow explicit submit via our primary button
    if (!submitter || submitter.getAttribute('data-explicit-submit') !== 'true') {
      return;
    }
    const error = await submitForm();
    if (error) {
      onError(error);
    } else {
      onComplete();
    }
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <Step1PersonalInfo
            formData={formData}
            loading={loading}
            updateField={updateField}
            wiggleFields={wiggleFields}
          />
        );
      case 2:
        return (
          <Step2HowFoundUs
            formData={formData}
            loading={loading}
            updateField={updateField}
            wiggleFields={wiggleFields}
          />
        );
      case 3:
        return (
          <Step3AccountType
            formData={formData}
            loading={loading}
            updateField={updateField}
            wiggleFields={wiggleFields}
          />
        );
      case 4:
        return (
          <Step4Role
            formData={formData}
            loading={loading}
            updateField={updateField}
            wiggleFields={wiggleFields}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-theme-light-page-bg dark:bg-theme-dark-page-bg">
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="w-full max-w-xl sm:max-w-2xl">
          <div className="bg-theme-light-bg dark:bg-theme-dark-bg rounded-xl border border-gray-300 dark:border-gray-600 overflow-hidden">
            {/* Header */}
            <div className="px-4 sm:px-5 pt-6 pb-4 text-center">
              <h1 className="text-2xl font-semibold text-theme-light-text-primary dark:text-theme-dark-text-primary mb-1">
                Complete Your Profile
              </h1>
              <p className="text-sm text-theme-light-text-secondary dark:text-theme-dark-text-secondary">
                Please provide your information to personalize your experience
              </p>
            </div>

            {/* Step Indicator */}
            <div className="px-4 sm:px-5 pb-3">
              <StepIndicator currentStep={currentStep} totalSteps={4} />
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="px-4 sm:px-5 pb-6">
              {/* Current Step Content */}
              <div>{renderCurrentStep()}</div>

              {/* Navigation Buttons */}
              <div className="flex justify-between items-center mt-6 pt-3 border-t border-gray-200 dark:border-gray-700">
                {/* Back Button - only show after step 1 */}
                {currentStep > 1 ? (
                  <button
                    type="button"
                    onClick={prevStep}
                    disabled={loading}
                    className="flex items-center px-3.5 py-2 text-sm font-medium rounded-lg transition-colors text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <ChevronLeft className="w-4 h-4 mr-1" />
                    Back
                  </button>
                ) : (
                  <div /> /* Empty div to maintain flex layout */
                )}

                {/* Next/Submit Button */}
                {currentStep < 4 ? (
                  <button
                    type="button"
                    onClick={(e) => {
                      if (!isCurrentStepValid && !loading) {
                        e.preventDefault();
                        handleDisabledNextClick();
                      } else if (isCurrentStepValid && !loading) {
                        handleNext();
                      }
                    }}
                    className={`flex items-center px-5 py-2 bg-[#E34290] text-white text-sm font-medium rounded-lg hover:bg-[#E34290]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#E34290] transition-colors ${
                      !isCurrentStepValid || loading ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    Next
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </button>
                ) : (
                  <button
                    type="submit"
                    onClick={(e) => {
                      if (loading || !isCurrentStepValid || !isFormValid) {
                        e.preventDefault();
                        handleDisabledCompleteClick();
                      }
                      // If valid, let the form submit naturally
                    }}
                    className={`flex items-center px-5 py-2 bg-[#E34290] text-white text-sm font-medium rounded-lg hover:bg-[#E34290]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#E34290] transition-colors ${
                      loading || !isCurrentStepValid || !isFormValid
                        ? 'opacity-50 cursor-not-allowed'
                        : ''
                    }`}
                    data-explicit-submit="true"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      'Complete Profile'
                    )}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
